// Contact-list composer — modal that collapses "create the list" and
// "add the people" into a single moment. Replaces the inline
// ContactListForm + the second-trip-to-detail-page for static rosters.
//
// Static type → search + lifecycle-stage filter + multi-select picker.
// Dynamic type → polished filter editor with live preview of who matches
// right now (the saved filter re-evaluates on every consumer).
//
// The composer reuses the existing backend filter_dsl shape (the same 6
// keys ContactListForm exposes today: lifecycle_stage, owner_id,
// account_id, q, since, until). Type is locked in edit mode (backend
// CHECK constraint).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import { Cloud, Layers, Search, Sparkles, X } from 'lucide-react';

import { APP_NAME } from '../../config/region';
import { accountsAPI } from '../../services/accountsAPI';
import { workspaceAPI } from '../../services/api';
import { contactsAPI } from '../../services/contactsAPI';
import {
  asCrmApiError,
  type CRMImportDedupe,
  type CRMRemoteContactSummary,
  crmSyncAPI,
} from '../../services/crmSyncService';
import type { Account, Contact } from '../../types/contact';
import {
  LIFECYCLE_STAGES,
  LIFECYCLE_STAGE_LABELS,
  type LifecycleStage,
} from '../../types/contact';
import type { WorkspaceMember } from '../../types';
import {
  ContactListCreate,
  ContactListFilterDsl,
  ContactListType,
  ContactListUpdate,
} from '../../types/contactList';
import {
  Button,
  Callout,
  Input,
  Pill,
  Select,
  Textarea,
} from '../../ui';

// LT-397 — UI-only 3-way mode. Maps to ContactListType at submit time:
// 'manual' and 'from_crm' both produce a static list; 'dynamic' is
// passed through. The sub-mode is necessary because two different
// pickers (local-search vs CRM-search) feed the same list_type.
type SourceMode = 'manual' | 'from_crm' | 'dynamic';

function uiToListType(mode: SourceMode): ContactListType {
  return mode === 'dynamic' ? 'dynamic' : 'static';
}

function listTypeToUi(t: ContactListType): SourceMode {
  return t === 'dynamic' ? 'dynamic' : 'manual';
}

export type ContactListComposerMode = 'create' | 'edit';

export interface ContactListComposerInitialValues {
  id?: string;
  name?: string;
  description?: string;
  list_type?: ContactListType;
  filter_dsl?: ContactListFilterDsl | null;
}

export interface ContactListComposerSubmit {
  mode: ContactListComposerMode;
  payload: ContactListCreate | ContactListUpdate;
  /** Static-only: contact ids to add as initial members on create. */
  initialMemberIds?: string[];
  /**
   * LT-397 — when the user picked "From CRM" mode the submission
   * carries the CRM external IDs to import. Parent creates the list
   * first (with list_type='static'), then calls
   * ``crmSyncAPI.importToList`` to pull those rows in.
   */
  crmImport?: {
    externalIds: string[];
    dedupe: CRMImportDedupe;
  };
}

export interface ContactListComposerProps {
  open: boolean;
  mode: ContactListComposerMode;
  initial?: ContactListComposerInitialValues;
  submitting: boolean;
  serverError?: string | null;
  onSubmit: (s: ContactListComposerSubmit) => void;
  onCancel: () => void;
}

interface FormState {
  name: string;
  description: string;
  // UI-only 3-way (manual / from_crm / dynamic). Translated to the
  // backend's 2-way ContactListType at submit time.
  source_mode: SourceMode;
  lifecycle_stage: '' | LifecycleStage;
  owner_id: string;
  account_id: string;
  q: string;
  since: string;
  until: string;
}

function isoOrUndefined(v: string): string | undefined {
  if (!v.trim()) return undefined;
  return v.length === 16 ? `${v}:00` : v;
}

function isoToLocal(value?: string): string {
  if (!value) return '';
  return value.slice(0, 16);
}

function buildFilter(s: FormState): ContactListFilterDsl | undefined {
  if (s.source_mode !== 'dynamic') return undefined;
  const f: ContactListFilterDsl = {};
  if (s.lifecycle_stage) f.lifecycle_stage = s.lifecycle_stage;
  if (s.owner_id) f.owner_id = s.owner_id;
  if (s.account_id) f.account_id = s.account_id;
  if (s.q.trim()) f.q = s.q.trim();
  const since = isoOrUndefined(s.since);
  const until = isoOrUndefined(s.until);
  if (since) f.since = since;
  if (until) f.until = until;
  return Object.keys(f).length ? f : undefined;
}

function isFilterEmpty(s: FormState): boolean {
  return buildFilter({ ...s, source_mode: 'dynamic' }) === undefined;
}

function contactDisplayName(c: Contact): string {
  const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return full || c.email || c.phone || 'Unnamed contact';
}

function contactInitials(c: Contact): string {
  const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  if (full) {
    return full
      .split(/\s+/)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }
  return (c.email ?? c.phone ?? '?').slice(0, 2).toUpperCase();
}

const TypeChooser: React.FC<{
  value: SourceMode;
  onChange: (v: SourceMode) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const options: {
    id: SourceMode;
    title: string;
    pillIntent: 'neutral' | 'info' | 'accent';
    pillText: string;
    copy: string;
    Icon: LucideIcon;
  }[] = [
    {
      id: 'manual',
      title: 'Static',
      pillIntent: 'neutral',
      pillText: 'explicit roster',
      copy: 'Pick a fixed set of contacts now. The roster doesn’t change unless you edit it.',
      Icon: Layers,
    },
    {
      id: 'from_crm',
      title: 'From CRM',
      pillIntent: 'accent',
      pillText: 'imported',
      copy: 'Pull contacts from your connected CRM into a fresh static list.',
      Icon: Cloud,
    },
    {
      id: 'dynamic',
      title: 'Dynamic',
      pillIntent: 'info',
      pillText: 'saved filter',
      copy: 'Define a filter. Membership re-evaluates every time the list is consumed.',
      Icon: Sparkles,
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-2.5">
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(o.id)}
            aria-pressed={active}
            className={[
              'text-left rounded-ds-md border p-3 transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1',
              active
                ? 'border-accent-border bg-accent-tint shadow-ds-glow-accent'
                : 'border-white/[0.08] bg-white/[0.02] hover:bg-white/5',
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <o.Icon className="w-3.5 h-3.5 text-slate-300" strokeWidth={2} />
              <strong className="text-[13px] text-slate-50">{o.title}</strong>
              {active ? (
                <span className="ml-auto text-[11px] text-accent-text">✓</span>
              ) : null}
            </div>
            <div className="mb-1.5">
              <Pill intent={o.pillIntent}>{o.pillText}</Pill>
            </div>
            <div className="text-[11px] leading-relaxed text-slate-400">
              {o.copy}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const StaticPicker: React.FC<{
  selected: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: (rows: Contact[]) => void;
  onClearVisible: (rows: Contact[]) => void;
}> = ({ selected, onToggle, onSelectAll, onClearVisible }) => {
  const [query, setQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<'' | LifecycleStage>('');
  const [results, setResults] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await contactsAPI.list({
        q: query.trim() || undefined,
        lifecycle_stage: stageFilter || undefined,
        limit: 100,
      });
      setResults(rows);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, stageFilter]);

  // Debounced re-fetch when query / stage filter changes.
  useEffect(() => {
    const t = window.setTimeout(() => {
      void fetchResults();
    }, 220);
    return () => window.clearTimeout(t);
  }, [fetchResults]);

  const allChecked =
    results.length > 0 && results.every((c) => selected.has(c.id));

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex gap-2 mb-2.5 items-center">
        <div className="relative flex-1">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            strokeWidth={2}
          />
          <Input
            placeholder="Search by name, email, or company…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1"
            style={{ paddingLeft: 28 }}
            aria-label="Search contacts"
          />
        </div>
        <Select
          value={stageFilter}
          onChange={(e) =>
            setStageFilter(e.target.value as '' | LifecycleStage)
          }
          aria-label="Lifecycle stage filter"
          className="w-48"
        >
          <option value="">All lifecycle stages</option>
          {LIFECYCLE_STAGES.map((s) => (
            <option key={s} value={s}>
              {LIFECYCLE_STAGE_LABELS[s]}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex items-center gap-2.5 px-3 py-2 bg-surface-2 border border-b-0 border-white/[0.08] rounded-t-ds-md text-xs">
        <input
          type="checkbox"
          checked={allChecked}
          onChange={() => {
            if (allChecked) onClearVisible(results);
            else onSelectAll(results);
          }}
          disabled={results.length === 0}
          className="accent-accent"
          aria-label={allChecked ? 'Clear visible' : 'Select all visible'}
        />
        <span className="text-slate-400">
          <strong className="font-mono text-slate-50">{results.length}</strong>{' '}
          contacts shown
          {loading ? (
            <span className="text-slate-500"> · loading…</span>
          ) : null}
        </span>
        <span className="ml-auto text-slate-400 font-mono text-[11px]">
          {selected.size > 0 ? `${selected.size} selected` : 'none selected'}
        </span>
      </div>

      <div
        className="flex-1 min-h-0 overflow-auto border border-white/[0.08] rounded-b-ds-md bg-white/[0.015]"
        role="listbox"
        aria-multiselectable="true"
        aria-label="Contact picker results"
      >
        {results.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-slate-400">
            No contacts match.
            <br />
            <span className="text-[11px] text-slate-500">
              Try clearing the search or stage filter.
            </span>
          </div>
        ) : (
          results.map((c) => {
            const checked = selected.has(c.id);
            return (
              <label
                key={c.id}
                className={[
                  'flex items-center gap-3 px-3 py-2.5 border-b border-white/[0.08] cursor-pointer transition-colors',
                  checked ? 'bg-accent-tint/40' : 'hover:bg-white/[0.03]',
                ].join(' ')}
                role="option"
                aria-selected={checked}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(c.id)}
                  className="accent-accent shrink-0"
                />
                <div
                  className="w-7 h-7 rounded-full bg-surface-3 text-slate-200 text-[11px] font-semibold flex items-center justify-center shrink-0"
                  aria-hidden="true"
                >
                  {contactInitials(c)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-slate-50 font-medium truncate">
                    {contactDisplayName(c)}
                  </div>
                  <div className="text-[11px] text-slate-400 font-mono truncate">
                    {c.email || c.phone || '—'}
                  </div>
                </div>
                <Pill intent="neutral" className="font-mono">
                  {LIFECYCLE_STAGE_LABELS[c.lifecycle_stage]}
                </Pill>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

const DynamicBuilder: React.FC<{
  state: FormState;
  setState: React.Dispatch<React.SetStateAction<FormState>>;
  members: WorkspaceMember[];
  accounts: Account[];
}> = ({ state, setState, members, accounts }) => {
  const memberLabel = (m: WorkspaceMember) => {
    const full = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
    return full || m.email;
  };

  return (
    <div className="flex flex-col gap-3 p-3 bg-white/[0.015] border border-white/[0.08] rounded-ds-md">
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <span>Match contacts where</span>
        <Pill intent="info">ALL</Pill>
        <span>of these are true:</span>
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <Select
          label="Lifecycle stage"
          value={state.lifecycle_stage}
          onChange={(e) =>
            setState({
              ...state,
              lifecycle_stage: e.target.value as '' | LifecycleStage,
            })
          }
        >
          <option value="">Any stage</option>
          {LIFECYCLE_STAGES.map((s) => (
            <option key={s} value={s}>
              {LIFECYCLE_STAGE_LABELS[s]}
            </option>
          ))}
        </Select>

        <Select
          label="Owner"
          value={state.owner_id}
          onChange={(e) => setState({ ...state, owner_id: e.target.value })}
        >
          <option value="">Any owner</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {memberLabel(m)}
            </option>
          ))}
        </Select>

        <Select
          label="Account"
          value={state.account_id}
          onChange={(e) => setState({ ...state, account_id: e.target.value })}
        >
          <option value="">Any account</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>

        <Input
          label="Search (name / email)"
          value={state.q}
          onChange={(e) => setState({ ...state, q: e.target.value })}
          placeholder="name@…"
        />

        <Input
          type="datetime-local"
          label="Created since"
          value={state.since}
          onChange={(e) => setState({ ...state, since: e.target.value })}
        />
        <Input
          type="datetime-local"
          label="Created until"
          value={state.until}
          onChange={(e) => setState({ ...state, until: e.target.value })}
        />
      </div>
    </div>
  );
};

const DynamicPreview: React.FC<{ filter: ContactListFilterDsl | undefined }> = ({
  filter,
}) => {
  const [results, setResults] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);

  // Re-run live preview as the filter changes; debounced to avoid
  // hammering the API while the user types.
  useEffect(() => {
    const t = window.setTimeout(async () => {
      if (!filter) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const rows = await contactsAPI.list({ ...filter, limit: 50 });
        setResults(rows);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [filter]);

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex items-center gap-2.5 px-3 py-2 bg-surface-2 border border-b-0 border-white/[0.08] rounded-t-ds-md text-xs">
        <Pill intent="info">live</Pill>
        <span className="text-slate-300">
          <strong className="font-mono text-info-text">
            {filter ? results.length : 0}
          </strong>{' '}
          {filter ? 'match right now' : 'add a filter to see matches'}
          {loading ? (
            <span className="text-slate-500"> · evaluating…</span>
          ) : null}
        </span>
        <span className="ml-auto font-mono text-[11px] text-slate-500">
          re-evaluates on every consumer
        </span>
      </div>
      <div className="flex-1 min-h-0 overflow-auto border border-white/[0.08] rounded-b-ds-md bg-white/[0.015]">
        {!filter ? (
          <div className="px-3 py-8 text-center text-xs text-slate-400">
            Add at least one rule above to preview matches.
          </div>
        ) : results.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-slate-400">
            No contacts match these rules yet.
            <br />
            <span className="text-[11px] text-slate-500">
              Loosen a filter to see matches.
            </span>
          </div>
        ) : (
          results.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-2.5 px-3 py-2 border-b border-white/[0.08]"
            >
              <div
                className="w-6 h-6 rounded-full bg-surface-3 text-slate-200 text-[10px] font-semibold flex items-center justify-center shrink-0"
                aria-hidden="true"
              >
                {contactInitials(c)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-50 truncate">
                  {contactDisplayName(c)}
                </div>
                <div className="text-[10px] text-slate-400 font-mono truncate">
                  {c.email || c.phone || '—'}
                </div>
              </div>
              <Pill intent="neutral" className="font-mono">
                {LIFECYCLE_STAGE_LABELS[c.lifecycle_stage]}
              </Pill>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// LT-397 — picker for the "From CRM" source mode. Mirrors StaticPicker
// (search box, multi-select, results panel) but the rows come from
// ``GET /crm/contacts`` and the selection is keyed by the CRM's
// ``external_id`` instead of a local Contact UUID.
const CrmContactPicker: React.FC<{
  selectedIds: Set<string>;
  selectedRows: Map<string, CRMRemoteContactSummary>;
  dedupe: CRMImportDedupe;
  onDedupeChange: (d: CRMImportDedupe) => void;
  onToggle: (row: CRMRemoteContactSummary) => void;
  onSelectAll: (rows: CRMRemoteContactSummary[]) => void;
  onClearVisible: (rows: CRMRemoteContactSummary[]) => void;
}> = ({
  selectedIds,
  selectedRows,
  dedupe,
  onDedupeChange,
  onToggle,
  onSelectAll,
  onClearVisible,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CRMRemoteContactSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await crmSyncAPI.listContacts({ q: query, limit: 50 });
      setResults(r.contacts);
    } catch (e) {
      const parsed = asCrmApiError(e);
      // 404 here means "no active CRM connection" — surface it as an
      // empty-state CTA, not a red error banner.
      const ax = e as { response?: { status?: number } };
      if (ax?.response?.status === 404) {
        setError('no_connection');
      } else if (ax?.response?.status === 501) {
        setError('not_supported_by_provider');
      } else {
        setError(parsed.message || 'Failed to load CRM contacts.');
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void fetchResults();
    }, 300);
    return () => window.clearTimeout(t);
  }, [fetchResults]);

  const allChecked =
    results.length > 0 && results.every((c) => selectedIds.has(c.external_id));

  const renderError = () => {
    if (error === 'no_connection') {
      return (
        <div className="px-3 py-8 text-center text-xs text-slate-300">
          No CRM is connected to this workspace yet.
          <br />
          <a
            href="/settings/integrations/crm"
            className="text-accent-text underline hover:text-accent"
          >
            Connect a CRM first
          </a>
          {' '}to import contacts from there.
        </div>
      );
    }
    if (error === 'not_supported_by_provider') {
      return (
        <div className="px-3 py-8 text-center text-xs text-slate-400">
          On-demand contact listing isn’t supported for the connected CRM yet.
        </div>
      );
    }
    return (
      <div role="alert" className="px-3 py-8 text-center text-xs text-danger-text">
        {error}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex gap-2 mb-2.5 items-center">
        <div className="relative flex-1">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            strokeWidth={2}
          />
          <Input
            placeholder="Search CRM contacts by name, email, or company…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1"
            style={{ paddingLeft: 28 }}
            aria-label="Search CRM contacts"
          />
        </div>
        <Select
          value={dedupe}
          onChange={(e) => onDedupeChange(e.target.value as CRMImportDedupe)}
          aria-label="Dedupe mode"
          className="w-44"
        >
          <option value="by_email">Skip dupes by email</option>
          <option value="by_phone">Skip dupes by phone</option>
          <option value="none">Always create new</option>
        </Select>
      </div>

      <div className="flex items-center gap-2.5 px-3 py-2 bg-surface-2 border border-b-0 border-white/[0.08] rounded-t-ds-md text-xs">
        <input
          type="checkbox"
          checked={allChecked}
          onChange={() => {
            if (allChecked) onClearVisible(results);
            else onSelectAll(results);
          }}
          disabled={results.length === 0}
          className="accent-accent"
          aria-label={allChecked ? 'Clear visible' : 'Select all visible'}
        />
        <span className="text-slate-400">
          <strong className="font-mono text-slate-50">{results.length}</strong>{' '}
          CRM contacts shown
          {loading ? <span className="text-slate-500"> · loading…</span> : null}
        </span>
        <span className="ml-auto text-slate-400 font-mono text-[11px]">
          {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'none selected'}
        </span>
      </div>

      <div
        className="flex-1 min-h-0 overflow-auto border border-white/[0.08] rounded-b-ds-md bg-white/[0.015]"
        role="listbox"
        aria-multiselectable="true"
        aria-label="CRM contact picker results"
        data-testid="crm-contact-picker-results"
      >
        {error
          ? renderError()
          : results.length === 0
            ? (
              <div className="px-3 py-8 text-center text-xs text-slate-400">
                {loading ? 'Loading…' : 'No CRM contacts match.'}
              </div>
            )
            : (
              results.map((c) => {
                const checked = selectedIds.has(c.external_id);
                const display = (
                  [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
                  || c.email || c.phone || 'Unnamed contact'
                );
                return (
                  <label
                    key={c.external_id}
                    className={[
                      'flex items-center gap-3 px-3 py-2.5 border-b border-white/[0.08] cursor-pointer transition-colors',
                      checked ? 'bg-accent-tint/40' : 'hover:bg-white/[0.03]',
                    ].join(' ')}
                    role="option"
                    aria-selected={checked}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(c)}
                      className="accent-accent shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-slate-50 font-medium truncate">
                        {display}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono truncate">
                        {c.email || c.phone || '—'}
                        {c.company ? ` · ${c.company}` : ''}
                      </div>
                    </div>
                    <Pill intent="accent" className="font-mono uppercase">
                      {c.source}
                    </Pill>
                  </label>
                );
              })
            )}
      </div>
      {selectedRows.size > 0 ? (
        <div className="mt-2.5 text-[11px] text-slate-400">
          On Create: pulled from CRM → new local Contacts (or skipped per
          dedupe rule) → added to this list.
        </div>
      ) : null}
    </div>
  );
};

const ContactListComposer: React.FC<ContactListComposerProps> = ({
  open,
  mode,
  initial,
  submitting,
  serverError,
  onSubmit,
  onCancel,
}) => {
  const initFilter = initial?.filter_dsl ?? undefined;
  const [state, setState] = useState<FormState>({
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    source_mode: listTypeToUi(initial?.list_type ?? 'static'),
    lifecycle_stage:
      (initFilter?.lifecycle_stage as LifecycleStage | undefined) ?? '',
    owner_id: initFilter?.owner_id ?? '',
    account_id: initFilter?.account_id ?? '',
    q: initFilter?.q ?? '',
    since: isoToLocal(initFilter?.since),
    until: isoToLocal(initFilter?.until),
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // LT-397 — independent picker state for "From CRM". Stores the
  // selected RemoteContactSummary rows so the parent can re-display
  // them in the result panel after import lands.
  const [crmSelectedIds, setCrmSelectedIds] = useState<Set<string>>(new Set());
  const [crmSelectedRows, setCrmSelectedRows] = useState<
    Map<string, CRMRemoteContactSummary>
  >(new Map());
  const [crmDedupe, setCrmDedupe] = useState<CRMImportDedupe>('by_email');
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const initialStateRef = useRef(state);

  // Reset internal state whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    const init: FormState = {
      name: initial?.name ?? '',
      description: initial?.description ?? '',
      source_mode: listTypeToUi(initial?.list_type ?? 'static'),
      lifecycle_stage:
        (initial?.filter_dsl?.lifecycle_stage as LifecycleStage | undefined) ??
        '',
      owner_id: initial?.filter_dsl?.owner_id ?? '',
      account_id: initial?.filter_dsl?.account_id ?? '',
      q: initial?.filter_dsl?.q ?? '',
      since: isoToLocal(initial?.filter_dsl?.since),
      until: isoToLocal(initial?.filter_dsl?.until),
    };
    setState(init);
    initialStateRef.current = init;
    setSelected(new Set());
    setCrmSelectedIds(new Set());
    setCrmSelectedRows(new Map());
    setCrmDedupe('by_email');
    setValidationError(null);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [m, a] = await Promise.all([
          workspaceAPI.listMembers(),
          accountsAPI.list({ limit: 200 }),
        ]);
        if (!cancelled) {
          setMembers(Array.isArray(m) ? m : []);
          setAccounts(Array.isArray(a) ? a : []);
        }
      } catch {
        // Pickers fall back to "no options" — non-fatal.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Esc to cancel.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onCancel]);

  const filter = useMemo(() => buildFilter(state), [state]);
  const matchedCount = state.source_mode === 'manual'
    ? selected.size
    : state.source_mode === 'from_crm' ? crmSelectedIds.size : null;

  const handleTypeChange = useCallback(
    (next: SourceMode) => {
      if (next === state.source_mode) return;
      // Warn if switching would discard configured-but-unsaved work.
      const hasManualWork = selected.size > 0;
      const hasCrmWork = crmSelectedIds.size > 0;
      const hasDynamicWork = !isFilterEmpty(state);
      const dirty =
        (state.source_mode === 'manual' && hasManualWork) ||
        (state.source_mode === 'from_crm' && hasCrmWork) ||
        (state.source_mode === 'dynamic' && hasDynamicWork);
      if (
        dirty &&
        !window.confirm(
          'Switching the list type will discard the work on this side. Continue?',
        )
      ) {
        return;
      }
      setState((s) => ({ ...s, source_mode: next }));
      if (next === 'dynamic') {
        setSelected(new Set());
        setCrmSelectedIds(new Set());
        setCrmSelectedRows(new Map());
      } else if (next === 'manual') {
        setCrmSelectedIds(new Set());
        setCrmSelectedRows(new Map());
      } else if (next === 'from_crm') {
        setSelected(new Set());
      }
    },
    [state, selected, crmSelectedIds],
  );

  const handleToggleMember = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((rows: Contact[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.id));
      return next;
    });
  }, []);

  const handleClearVisible = useCallback((rows: Contact[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => next.delete(r.id));
      return next;
    });
  }, []);

  const canSubmit =
    state.name.trim().length > 0 &&
    (
      state.source_mode === 'manual'
      || state.source_mode === 'from_crm'
      || filter !== undefined
    ) &&
    // From-CRM requires at least one selected row at submit time
    (state.source_mode !== 'from_crm' || crmSelectedIds.size > 0) &&
    !submitting;

  const handleSubmit = useCallback(() => {
    const trimmedName = state.name.trim();
    if (!trimmedName) {
      setValidationError('Name is required.');
      return;
    }
    if (state.source_mode === 'dynamic' && !filter) {
      setValidationError(
        'Dynamic lists need at least one filter (lifecycle stage, owner, account, search, or date range).',
      );
      return;
    }
    if (state.source_mode === 'from_crm' && crmSelectedIds.size === 0) {
      setValidationError('Pick at least one CRM contact to import.');
      return;
    }
    setValidationError(null);

    if (mode === 'create') {
      const list_type: ContactListType = uiToListType(state.source_mode);
      const payload: ContactListCreate = {
        name: trimmedName,
        list_type,
        description: state.description.trim() || undefined,
        filter_dsl: filter,
      };
      onSubmit({
        mode: 'create',
        payload,
        initialMemberIds:
          state.source_mode === 'manual' && selected.size > 0
            ? Array.from(selected)
            : undefined,
        crmImport:
          state.source_mode === 'from_crm' && crmSelectedIds.size > 0
            ? {
                externalIds: Array.from(crmSelectedIds),
                dedupe: crmDedupe,
              }
            : undefined,
      });
    } else {
      const init = initialStateRef.current;
      const payload: ContactListUpdate = {
        name: trimmedName !== init.name ? trimmedName : undefined,
        description:
          state.description.trim() !== init.description
            ? state.description.trim() || undefined
            : undefined,
        filter_dsl: state.source_mode === 'dynamic' ? filter : undefined,
      };
      onSubmit({ mode: 'edit', payload });
    }
  }, [state, filter, selected, crmSelectedIds, crmDedupe, mode, onSubmit]);

  const handleCrmToggle = useCallback((row: CRMRemoteContactSummary) => {
    setCrmSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.external_id)) next.delete(row.external_id);
      else next.add(row.external_id);
      return next;
    });
    setCrmSelectedRows((prev) => {
      const next = new Map(prev);
      if (next.has(row.external_id)) next.delete(row.external_id);
      else next.set(row.external_id, row);
      return next;
    });
  }, []);

  const handleCrmSelectAll = useCallback((rows: CRMRemoteContactSummary[]) => {
    setCrmSelectedIds((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.external_id));
      return next;
    });
    setCrmSelectedRows((prev) => {
      const next = new Map(prev);
      rows.forEach((r) => next.set(r.external_id, r));
      return next;
    });
  }, []);

  const handleCrmClearVisible = useCallback((rows: CRMRemoteContactSummary[]) => {
    setCrmSelectedIds((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => next.delete(r.external_id));
      return next;
    });
    setCrmSelectedRows((prev) => {
      const next = new Map(prev);
      rows.forEach((r) => next.delete(r.external_id));
      return next;
    });
  }, []);

  if (!open) return null;

  const headerEyebrow = mode === 'create' ? 'NEW CONTACT LIST' : 'EDIT CONTACT LIST';
  const headerTitle =
    mode === 'create'
      ? 'Build a cohort'
      : initial?.name
        ? `Edit "${initial.name}"`
        : 'Edit list';

  // Portal to <body> so the modal escapes the AuthenticatedLayout's
  // flex column (whose left margin == sidebar width). Without this the
  // modal's `inset-0` is captured by the main column and the sidebar
  // crops the left edge of the composer.
  const node = (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-stretch bg-black/70 backdrop-blur-sm p-4 sm:p-8"
      onClick={() => !submitting && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-list-composer-title"
    >
      <div
        className="flex-1 bg-surface-1 border border-white/[0.16] rounded-ds-xl shadow-ds-e3 flex flex-col overflow-hidden max-w-[1600px] mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[0.08] flex items-center gap-3 shrink-0">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400">
              {headerEyebrow}
            </p>
            <h2
              id="contact-list-composer-title"
              className="text-base font-semibold text-slate-50 mt-0.5"
            >
              {headerTitle}
            </h2>
          </div>
          <Pill
            intent={
              state.source_mode === 'manual'
                ? 'neutral'
                : state.source_mode === 'from_crm'
                  ? 'accent'
                  : 'info'
            }
          >
            {state.source_mode === 'manual'
              ? 'Static · explicit roster'
              : state.source_mode === 'from_crm'
                ? 'Static · from CRM'
                : 'Dynamic · saved filter'}
          </Pill>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-400 font-mono">
              <kbd className="px-1.5 py-0.5 border border-white/[0.08] rounded text-[10px] bg-surface-2">
                esc
              </kbd>
              to close
            </span>
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close composer"
              className="p-1 rounded-ds-md text-slate-400 hover:bg-white/5 hover:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[440px_1fr] min-h-0">
          {/* LEFT — config */}
          <div className="px-6 py-5 md:border-r border-b md:border-b-0 border-white/[0.08] flex flex-col gap-5 overflow-auto bg-white/[0.01]">
            {mode === 'create' && (
              <div>
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2.5">
                  01 · Type
                </p>
                <TypeChooser
                  value={state.source_mode}
                  onChange={handleTypeChange}
                />
              </div>
            )}

            <div>
              <Input
                label={mode === 'create' ? '02 · Name' : 'Name'}
                value={state.name}
                onChange={(e) => setState({ ...state, name: e.target.value })}
                placeholder="VIP customers, Q3 trial leads…"
              />
            </div>

            <div>
              <Textarea
                label="Description (optional)"
                value={state.description}
                onChange={(e) =>
                  setState({ ...state, description: e.target.value })
                }
                rows={3}
                placeholder="What's this list for?"
              />
            </div>

            {state.source_mode === 'manual' ? (
              <Callout tone="info" title="Static — explicit roster">
                The roster freezes on Create. You can edit members later from
                the list detail page.
              </Callout>
            ) : state.source_mode === 'from_crm' ? (
              <Callout tone="info" title="From CRM — imported roster">
                Pulls the picked CRM contacts into {APP_NAME} as new local
                Contacts (deduped per the rule), then adds them to a fresh
                static list. The list is editable afterwards.
              </Callout>
            ) : (
              <Callout tone="info" title="Dynamic — saved filter">
                Membership re-evaluates every time the list is consumed by a
                campaign or sequence. The right-side preview is what matches
                right now.
              </Callout>
            )}

            {mode === 'edit' && (
              <Callout tone="neutral" title="Type is locked">
                A list&apos;s type can&apos;t change once it exists. Delete and
                recreate to switch.
              </Callout>
            )}

            <div className="mt-auto pt-4 border-t border-white/[0.08] flex flex-col gap-2.5">
              {(validationError || serverError) && (
                <p className="text-[11px] text-danger-text">
                  {validationError ?? serverError}
                </p>
              )}
              <div className="text-xs text-slate-400">
                {state.source_mode === 'manual' ? (
                  <>
                    Will be created with{' '}
                    <strong className="font-mono text-slate-50">
                      {selected.size}
                    </strong>{' '}
                    {selected.size === 1 ? 'contact' : 'contacts'}.
                  </>
                ) : state.source_mode === 'from_crm' ? (
                  <>
                    Will import{' '}
                    <strong className="font-mono text-slate-50">
                      {crmSelectedIds.size}
                    </strong>{' '}
                    CRM {crmSelectedIds.size === 1 ? 'contact' : 'contacts'}.
                  </>
                ) : (
                  <>
                    Filter is{' '}
                    <strong className="font-mono text-slate-50">
                      {filter ? 'ready' : 'empty'}
                    </strong>
                    .
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  intent="primary"
                  size="md"
                  className="flex-1 justify-center"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  {submitting
                    ? mode === 'create'
                      ? 'Creating…'
                      : 'Saving…'
                    : mode === 'create'
                      ? state.source_mode === 'manual'
                        ? `Create list · ${selected.size}`
                        : state.source_mode === 'from_crm'
                          ? `Import & create · ${crmSelectedIds.size}`
                          : 'Create dynamic list'
                      : 'Save'}
                </Button>
                <Button intent="ghost" size="md" onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>

          {/* RIGHT — members or rules + preview */}
          <div className="px-6 py-5 flex flex-col min-h-0 gap-3">
            <div className="flex items-center gap-2.5">
              <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400">
                {state.source_mode === 'dynamic' ? '03 · Rules' : '03 · Members'}
              </p>
              <span className="text-[13px] text-slate-300">
                {state.source_mode === 'manual'
                  ? mode === 'edit'
                    ? 'Member management lives on the detail page.'
                    : 'Pick contacts'
                  : state.source_mode === 'from_crm'
                    ? 'Pick CRM contacts to import'
                    : 'Define who matches'}
              </span>
              {state.source_mode !== 'dynamic'
                ? matchedCount !== null && (
                    <Pill intent="accent">
                      <span className="font-mono">{matchedCount}</span> selected
                    </Pill>
                  )
                : null}
              <span className="ml-auto text-[11px] font-mono text-slate-500 hidden md:inline">
                {state.source_mode === 'manual'
                  ? 'roster locks on Create'
                  : state.source_mode === 'from_crm'
                    ? 'imports run on Create'
                    : 're-evaluates on every consumer'}
              </span>
            </div>

            {state.source_mode === 'manual' ? (
              mode === 'edit' ? (
                <div className="flex-1 min-h-0 flex items-center justify-center text-center text-sm text-slate-400 border border-dashed border-white/[0.08] rounded-ds-md p-6">
                  <span>
                    Open the list detail page to add or remove members for
                    this static list.
                  </span>
                </div>
              ) : (
                <div className="flex-1 min-h-0 flex flex-col">
                  <StaticPicker
                    selected={selected}
                    onToggle={handleToggleMember}
                    onSelectAll={handleSelectAll}
                    onClearVisible={handleClearVisible}
                  />
                </div>
              )
            ) : state.source_mode === 'from_crm' ? (
              <div className="flex-1 min-h-0 flex flex-col">
                <CrmContactPicker
                  selectedIds={crmSelectedIds}
                  selectedRows={crmSelectedRows}
                  dedupe={crmDedupe}
                  onDedupeChange={setCrmDedupe}
                  onToggle={handleCrmToggle}
                  onSelectAll={handleCrmSelectAll}
                  onClearVisible={handleCrmClearVisible}
                />
              </div>
            ) : (
              <div className="flex-1 min-h-0 grid grid-rows-[auto_1fr] gap-3">
                <DynamicBuilder
                  state={state}
                  setState={setState}
                  members={members}
                  accounts={accounts}
                />
                <div className="min-h-0 flex flex-col">
                  <DynamicPreview filter={filter} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default ContactListComposer;
